import { useCallback, useEffect } from 'react';

import { toPng } from 'html-to-image';

import { useLoadingStore } from '~/store';

export const useImageDownload = () => {
  const { setLoading } = useLoadingStore((state) => state.actions);

  useEffect(() => {
    return () => {
      window.onImageDownloadResult = null;
    };
  }, []);

  /** 앱 로컬 푸시 표시 */
  const showNotification = (message: string) => {
    const params = JSON.stringify({
      body: message,
      action: 'MOVE_ALBUM',
    });

    if (window?.JSBridge) {
      window.JSBridge?.showNotification(params);
    } else if (window?.webkit) {
      window.webkit.messageHandlers.showNotification.postMessage(params);
    }
  };

  /** safari에서 다운로드 시 빈 화면이 다운로드되는 이슈 해소를 위해 최대 3번 시도 */
  const buildPng = async ({
    element,
    attemptCount,
    style,
  }: {
    element: HTMLElement;
    attemptCount: number;
    style?: Partial<CSSStyleDeclaration>;
  }) => {
    const isSafariAgent = navigator.vendor.includes('Apple') || !!window?.webkit;

    let dataUrl = '';
    let attempt = 0;
    const maxAttempts = isSafariAgent ? attemptCount : 1;

    const cycle: number[] = [];
    let repeat = true;

    while (repeat && attempt < maxAttempts) {
      dataUrl = await toPng(element, {
        type: 'image/png',
        backgroundColor: 'white',
        quality: 1,
        style: {
          transform: 'none',
          ...style,
        },
        filter: () => {
          return true;
        },
      });
      attempt += 1;
      cycle[attempt] = dataUrl.length;

      if (cycle[attempt - 1] && dataUrl.length > cycle[attempt - 1]) repeat = false;
    }

    return dataUrl;
  };

  /** 이미지 다운로드 */
  const downloadImage = useCallback(
    async ({
      element,
      fileName,
      attemptCount = 3,
      onSuccess,
      onError,
      style,
    }: {
      element: HTMLElement;
      fileName: string;
      attemptCount?: number;
      onSuccess: () => void;
      onError: () => void;
      style?: Partial<CSSStyleDeclaration>;
    }) => {
      window.onImageDownloadResult = (isSuccess: string | boolean) => {
        if (String(isSuccess) === 'true') {
          onSuccess();
        } else {
          onError();
        }
      };

      try {
        setLoading({ show: true, dimmed: true, zIndex: 99999 });
        const dataUrl = await buildPng({ element, attemptCount, style });

        if (dataUrl) {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
            ctx.drawImage(img, 0, 0);

            const pngFile = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.download = `${fileName}.png`;
            downloadLink.href = `${pngFile}`;
            downloadLink.click();
          };
          img.src = dataUrl;
        } else {
          throw new Error('이미지 변환 중 오류가 발생했습니다. 다시 시도해주세요.');
        }
      } catch (error) {
        console.error(error);
        onError();
      } finally {
        setLoading({ show: false, dimmed: true, zIndex: 99999 });
      }
    },
    [setLoading]
  );

  return {
    downloadImage,
    showNotification,
  };
};
